import { useState, useEffect, useMemo } from "react";
import { getTodaysOperationsDataHttp, saveTodaysOperationsUpdateHttp, getOperationsHistoryHttp } from "../../../http/renewalHttp/RenewalHttp";
import Div from "../../../components/Div";
import PageTitle from "../../../components/PageTitle";
import { Badge, Pagination, Modal, Button } from 'react-bootstrap';

const getTodayISO = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const convertGMTtoIST = (gmtValue) => {
    if (!gmtValue) return '--';

    try {
        const gmtDate = new Date(gmtValue);

        // Add 5 hours 30 mins
        const istDate = new Date(gmtDate.getTime() + (5.5 * 60 * 60 * 1000));

        const hh = String(istDate.getHours()).padStart(2, '0');
        const mm = String(istDate.getMinutes()).padStart(2, '0');
        const ss = String(istDate.getSeconds()).padStart(2, '0');

        const h12 = istDate.getHours() % 12 || 12;
        const ampm = istDate.getHours() >= 12 ? 'PM' : 'AM';

        return `${hh}:${mm}:${ss} (${h12}:${mm}:${ss} ${ampm})`;
    } catch (err) {
        return '--';
    }
};

const extractGMTTime = (gmtValue) => {
    if (!gmtValue) return '--';

    try {
        const gmtDate = new Date(gmtValue);

        const hh = String(gmtDate.getHours()).padStart(2, '0');
        const mm = String(gmtDate.getMinutes()).padStart(2, '0');
        const ss = String(gmtDate.getSeconds()).padStart(2, '0');

        const h12 = gmtDate.getHours() % 12 || 12;
        const ampm = gmtDate.getHours() >= 12 ? 'PM' : 'AM';

        return `${hh}:${mm}:${ss} ${ampm}`;
    } catch (err) {
        return '--';
    }
};
// ─── FIX 1: Deduplicate rows by subscriptionId, keeping the row with the most
//            data (prefers rows that have malvika_comments / approval_status).
const deduplicateBySubscriptionId = (rows) => {
    const map = new Map();
    rows.forEach((row) => {
        const key = row.subscriptionId;
        if (!map.has(key)) {
            map.set(key, row);
        } else {
            const existing = map.get(key);
            // Prefer the row that has ops data filled in
            const existingScore = (existing.Malvika_Comments ? 1 : 0) + (existing.approval_status ? 1 : 0);
            const newScore = (row.Malvika_Comments ? 1 : 0) + (row.approval_status ? 1 : 0);
            if (newScore > existingScore) {
                map.set(key, row);
            }
        }
    });
    return Array.from(map.values());
};

// ─── Reads the flag (handles either case from API, defaults to 0/false)
const getFlag = (item) => {
    const raw = item.Flag !== undefined ? item.Flag : item.flag;
    return Number(raw) === 1;
};

// ─── Reads the Ops-side last-updated timestamp (used to order flag=1 rows,
//     most-recently-reset first)
const getOpsUpdatedAt = (item) => {
    const raw = item.Ops_Updated_At || item.ops_updated_at || null;
    return raw ? new Date(raw).getTime() : 0;
};

export default function GetTodaysOperations() {
    const [opsData, setOpsData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [searchTerm, setSearchTerm] = useState("");

    const [startDate, setStartDate] = useState(getTodayISO());
    const [endDate, setEndDate] = useState(getTodayISO());

    // ─── FIX 2: editedRows is keyed by subscriptionId – single source of truth.
    //            Each field is stored only once, so no double-rendering.
    const [editedRows, setEditedRows] = useState({});

    // ─── FIX 3: savedKeys tracks subscriptionIds that were just saved so we can
    //            push them to the bottom of the sorted list immediately.
    const [savedKeys, setSavedKeys] = useState(new Set());

    // History Modal State
    const [showHistory, setShowHistory] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyDomain, setHistoryDomain] = useState("");

    const loadOpsData = async () => {
        setLoading(true);
        try {
            const payload = { start_date: startDate, end_date: endDate };
            const res = await getTodaysOperationsDataHttp(payload);
            if (res && res.data) {
                // ─── Deduplicate immediately after fetch
                setOpsData(deduplicateBySubscriptionId(res.data));
            }
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setStartDate(getTodayISO());
        setEndDate(getTodayISO());
        setSearchTerm("");
        setCurrentPage(1);
        setSavedKeys(new Set());
        setTimeout(() => loadOpsData(), 50);
    };

    useEffect(() => { loadOpsData(); }, []);

    const sortedAndFilteredData = useMemo(() => {
        const safeSearch = (searchTerm || "").trim().toLowerCase();

        let processedList = opsData.filter(item => {
            if (!safeSearch) return true;
            return String(item.Domains || "").toLowerCase().includes(safeSearch);
        });

        // ── Helpers: field names may come as either case from the API ──────────
        const getActualDate = (item) =>
            (item.Actual_Date || item.actual_date || "").substring(0, 10);

        const getApprovalStatus = (item) =>
            item.approval_status || item.Approval_Status || "Pending";

        const getMalvikaComment = (item) =>
            item.Malvika_Comments || item.malvika_comments || "";

        const todayISO = getTodayISO();


        const isSnoozedFn = (item) => {
            const actual = getActualDate(item);
            if (!actual) return false;
            const srd = (item.shivaami_renewal_date || "").substring(0, 10);
            return actual === todayISO && actual !== srd;
        };



        const isProcessedFn = (item) => {
            if (isSnoozedFn(item)) return false;
            const status = item.approval_status || item.Approval_Status;
            const comment = (
                item.Malvika_Comments ||
                item.malvika_comments || ""
            ).trim();

            const actual = (
                item.Actual_Date ||
                item.actual_date ||
                ""
            ).substring(0, 10);

            return (
                status === "Approved" ||
                status === "Rejected" ||
                comment !== "" ||
                actual !== ""
            );
        };

        const isUpdated = (item) => {
    const status = item.approval_status || item.Approval_Status || "Pending";

    const comment = (
        item.Malvika_Comments ||
        item.malvika_comments ||
        ""
    ).trim();

    return (
        status !== "Pending" ||
        comment !== ""
    );
};

        return processedList.sort((a, b) => {

    const today = getTodayISO();

    // ─── FLAG CHECK FIRST: rows that were just reset (flag=1) always go to
    //     the very top, above every other group, sorted by most-recently
    //     reset first (Ops_Updated_At descending).
    const flagA = getFlag(a);
    const flagB = getFlag(b);

    if (flagA && !flagB) return -1;
    if (!flagA && flagB) return 1;
    if (flagA && flagB) {
        return getOpsUpdatedAt(b) - getOpsUpdatedAt(a);
    }

    const grdA = (
        a.Actual_Date ||
        a.actual_date ||
        ""
    ).substring(0, 10);

    const grdB = (
        b.Actual_Date ||
        b.actual_date ||
        ""
    ).substring(0, 10);

    const srdA = (
        a.shivaami_renewal_date ||
        ""
    ).substring(0, 10);

    const srdB = (
        b.shivaami_renewal_date ||
        ""
    ).substring(0, 10);

    const updatedA = isUpdated(a);
    const updatedB = isUpdated(b);

    // Updated domains always at bottom
    if (!updatedA && updatedB) return -1;
    if (updatedA && !updatedB) return 1;

    const getGroup = (grd, srd) => {

        // 1. Past GRD
        if (grd && grd < today) return 1;

        // 2. Today's GRD
        if (grd === today) return 2;

        // 3. Today's SRD
        if ((!grd || grd === "") && srd === today) return 3;

        // 4. Future GRD
        if (grd && grd > today) return 4;

        return 5;
    };

    const groupA = getGroup(grdA, srdA);
    const groupB = getGroup(grdB, srdB);

    if (groupA !== groupB) {
        return groupA - groupB;
    }

    // Past GRD
    if (groupA === 1) {
        const grdCompare =
            new Date(grdA) - new Date(grdB);

        if (grdCompare !== 0) {
            return grdCompare;
        }

        return new Date(srdA) - new Date(srdB);
    }

    // Today's GRD
    if (groupA === 2) {
        return new Date(srdA) - new Date(srdB);
    }

    // Today's SRD
    if (groupA === 3) {
        return new Date(srdA) - new Date(srdB);
    }

    // Future GRD
    if (groupA === 4) {
        const grdCompare =
            new Date(grdA) - new Date(grdB);

        if (grdCompare !== 0) {
            return grdCompare;
        }

        return new Date(srdA) - new Date(srdB);
    }

    return new Date(srdA) - new Date(srdB);
}

        // return processedList.sort((a, b) => {

        //     if (safeSearch !== "") {
        //         return String(a.Domains || "").localeCompare(
        //             String(b.Domains || "")
        //         );
        //     }

        //     const snoozedA = isSnoozedFn(a);
        //     const snoozedB = isSnoozedFn(b);

        //     // Snoozed always on top
        //     if (snoozedA && !snoozedB) return -1;
        //     if (!snoozedA && snoozedB) return 1;

        //     // Approved / Rejected always at bottom
        //     const statusA = a.approval_status || a.Approval_Status || "";
        //     const statusB = b.approval_status || b.Approval_Status || "";

        //     const isFinalA =
        //         statusA === "Approved" || statusA === "Rejected";

        //     const isFinalB =
        //         statusB === "Approved" || statusB === "Rejected";

        //     if (!isFinalA && isFinalB) return -1;
        //     if (isFinalA && !isFinalB) return 1;

        //     const processedA = isProcessedFn(a);
        //     const processedB = isProcessedFn(b);

        //     // Pending rows first
        //     if (!processedA && processedB) return -1;
        //     if (processedA && !processedB) return 1;

        //     // Pending group sorting
        //     if (!processedA && !processedB) {
        //         return (
        //             new Date(a.shivaami_renewal_date || 0) -
        //             new Date(b.shivaami_renewal_date || 0)
        //         );
        //     }

        //     // Within Approved / Rejected group
        //     if (isFinalA && isFinalB) {

        //         // Approved above Rejected
        //         if (statusA === "Approved" && statusB === "Rejected") return -1;
        //         if (statusA === "Rejected" && statusB === "Approved") return 1;

        //         const dateA =
        //             a.updated_at ||
        //             a.Actual_Date ||
        //             a.actual_date ||
        //             "9999-12-31";

        //         const dateB =
        //             b.updated_at ||
        //             b.Actual_Date ||
        //             b.actual_date ||
        //             "9999-12-31";

        //         return new Date(dateA) - new Date(dateB);
        //     }

        //     // Other processed rows
        //     const dateA =
        //         a.updated_at ||
        //         a.Actual_Date ||
        //         a.actual_date ||
        //         "9999-12-31";

        //     const dateB =
        //         b.updated_at ||
        //         b.Actual_Date ||
        //         b.actual_date ||
        //         "9999-12-31";

        //     return new Date(dateA) - new Date(dateB);
        // }
        

            // return processedList.sort((a, b) => {

            //     if (safeSearch !== "") {
            //         return String(a.Domains || "").localeCompare(
            //             String(b.Domains || "")
            //         );
            //     }

            //     const snoozedA = isSnoozedFn(a);
            //     const snoozedB = isSnoozedFn(b);

            //     if (snoozedA && !snoozedB) return -1;
            //     if (!snoozedA && snoozedB) return 1;

            //     const processedA = isProcessedFn(a);
            //     const processedB = isProcessedFn(b);

            //     // Pending rows first
            //     if (!processedA && processedB) return -1;
            //     if (processedA && !processedB) return 1;

            //     // Pending group
            //     if (!processedA && !processedB) {
            //         return new Date(a.shivaami_renewal_date || 0)
            //             - new Date(b.shivaami_renewal_date || 0);
            //     }

            //     // Processed group
            //     const dateA =
            //         a.updated_at ||
            //         a.Actual_Date ||
            //         a.actual_date ||
            //         "9999-12-31";

            //     const dateB =
            //         b.updated_at ||
            //         b.Actual_Date ||
            //         b.actual_date ||
            //         "9999-12-31";

            //     return new Date(dateA) - new Date(dateB);
            // }
        );
    }, [opsData, searchTerm, savedKeys]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    console.log(
        sortedAndFilteredData.map(x => ({
            domain: x.Domains,
            id: x.subscriptionId,
            flag: getFlag(x)
        }))
    );
    const currentItems = sortedAndFilteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    // ─── FIX 2: Single handler — writes to editedRows[subscriptionId][field] only
    const handleInputChange = (subscriptionId, field, value) => {
        setEditedRows(prev => ({
            ...prev,
            [subscriptionId]: {
                ...prev[subscriptionId],
                [field]: value
            }
        }));
    };

    const handleSave = async (item) => {

        console.log("=================================");
        console.log("Saving Subscription:", item.subscriptionId);
        console.log("Item:", item);

        const edits = editedRows[item.subscriptionId] || {};

        const payload = {
            subscription_id: item.subscriptionId,
            domain_name: item.Domains,
            malvika_comments:
                edits.Malvika_Comments !== undefined
                    ? edits.Malvika_Comments
                    : (item.Malvika_Comments || ""),

            reprovision_date: "",

            actual_date:
                edits.Actual_Date !== undefined
                    ? edits.Actual_Date
                    : (item.Actual_Date
                        ? item.Actual_Date.substring(0, 10)
                        : ""),

            approval_status:
                edits.approval_status !== undefined
                    ? edits.approval_status
                    : (item.approval_status || "Pending"),

            email: localStorage.getItem("user")
        };

        console.log("Payload:", payload);

        const res = await saveTodaysOperationsUpdateHttp(payload);

        console.log("API Response:", res);

        if (res) {

            console.log("SAVE SUCCESS");

            // ─── Locally clear the flag immediately so the row drops out of
            //     the pinned-to-top group right away, without waiting for a refetch.
            setOpsData(prev => prev.map(row =>
                row.subscriptionId === item.subscriptionId
                    ? { ...row, Flag: 0, flag: 0 }
                    : row
            ));

            setSavedKeys(prev => {

                const updated = new Set([
                    ...prev,
                    String(item.subscriptionId)
                ]);

                console.log("Updated savedKeys:");
                console.log([...updated]);

                return updated;
            });

        } else {

            console.log("SAVE FAILED");

        }
    };

    const viewHistory = async (item) => {
        setHistoryDomain(item.Domains);
        const res = await getOperationsHistoryHttp(item.subscriptionId);
        if (res && res.data) {
            setHistoryLogs(res.data);
            setShowHistory(true);
        } else {
            alert("No history found or error fetching history.");
        }
    };

    // ── Shared helper: returns the "live" Actual_Date for a row,
    //    preferring any in-flight edit over the saved DB value.
    const getRowActualDate = (item) => {
        const edits = editedRows[item.subscriptionId] || {};
        const raw = edits.Actual_Date !== undefined
            ? edits.Actual_Date
            : (item.Actual_Date || "");
        return raw ? raw.substring(0, 10) : "";
    };

    const formatOpsStatus = (status) => {
        if (status === 'Confirmation Received' || status === 'Confirmation received for flex') return 'renew';
        return status || '--';
    };


    return (
        <Div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <PageTitle title="Daily Operations Tasks" />

                <div className="d-flex gap-2 align-items-center">
                    <input type="date" className="form-control form-control-sm border-secondary" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    <span className="fw-bold text-secondary">-</span>
                    <input type="date" className="form-control form-control-sm border-secondary" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    <button className="btn btn-primary btn-sm px-3" onClick={loadOpsData}>Filter</button>
                    <button className="btn btn-outline-secondary btn-sm px-3" onClick={handleReset}>Reset</button>
                    <input
                        type="text"
                        className="form-control form-control-sm border-primary ms-3"
                        placeholder=" Search Domain..."
                        value={searchTerm}
                        style={{ width: '200px' }}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                    <Badge bg="info" className="p-2 fs-6 ms-2">Total Records: {sortedAndFilteredData.length}</Badge>
                </div>
            </div>

            <Div className="col-md-12 mt-2">
                <div className="table-responsive bg-white shadow-sm rounded p-1" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-center py-5">Loading data...</div>
                    ) : (
                        <table key="main-table" className="table table-bordered table-hover align-middle mb-0">
                            <thead className="bg-primary text-white sticky-top" style={{ fontSize: '11px', zIndex: 10 }}>
                                <tr>
                                    <th>#</th>
                                    <th>Renewal Date (SRD)</th>
                                    <th>Domain</th>
                                    <th>Plan</th>
                                    <th>Licenses</th>
                                    <th>Renewed Users</th>
                                    <th>Status</th>
                                    <th>Google Date (GRD)</th>
                                    <th>Team</th>
                                    <th>Special Comment</th>
                                    <th>Ops Status</th>
                                    <th>Operations Team Comments</th>
                                    <th>GRD Cases</th>
                                    <th>Google Renewal GMT</th>
                                    <th>Renewal Time (IST)</th>
                                    <th>Action</th>
                                    <th>History</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: '11px' }}>
                                {currentItems.length > 0 ? currentItems.map((item, index) => {
                                    const rowEdits = editedRows[item.subscriptionId] || {};
                                    const todayISO = getTodayISO();

                                    // Read display values — prefer in-flight edit, fall back to DB
                                    const currentApprovalStatus = rowEdits.approval_status !== undefined
                                        ? rowEdits.approval_status
                                        : (item.approval_status || item.Approval_Status || 'Pending');

                                    const currentMalvikaComment = rowEdits.Malvika_Comments !== undefined
                                        ? rowEdits.Malvika_Comments
                                        : (item.Malvika_Comments || item.malvika_comments || '');

                                    const currentActualDate = rowEdits.Actual_Date !== undefined
                                        ? rowEdits.Actual_Date
                                        : ((item.Actual_Date || item.actual_date || '').substring(0, 10));

                                    // Row classification — mirrors sort helpers exactly
                                    // Use committed DB value (item.*) not in-flight edits,
                                    // so highlight only changes after Save (same as sort behaviour)
                                    const committedActual = (item.Actual_Date || item.actual_date || "").substring(0, 10);
                                    const srd = (item.shivaami_renewal_date || "").substring(0, 10);
                                    const committedStatus = item.approval_status || item.Approval_Status || "Pending";
                                    const committedComment = (item.Malvika_Comments || item.malvika_comments || "").trim();

                                    const isSnoozed = committedActual === todayISO && committedActual !== srd;
                                    const isFlagged = getFlag(item);

                                    const justSaved = savedKeys.has(item.subscriptionId) && !isSnoozed;

                                    const isProcessed = !isSnoozed && (
                                        justSaved ||
                                        (committedActual && committedActual > todayISO) ||
                                        committedStatus === "Approved" ||
                                        committedStatus === "Rejected" ||
                                        committedComment !== ""
                                    );

                                    let rowClass = "";
                                    if (isFlagged) rowClass = "table-danger";
                                    else if (isSnoozed) rowClass = "table-warning";
                                    else if (justSaved) rowClass = "table-success";
                                    else if (isProcessed) rowClass = "table-light";

                                    return (
                                        <tr key={`row-${item.subscriptionId}`} className={rowClass}>
                                            <td>
                                                {indexOfFirstItem + index + 1}
                                                {isFlagged && <div style={{ fontSize: '9px', color: '#721c24', marginTop: '4px', fontWeight: 'bold' }}>NEEDS REVIEW</div>}
                                                {isSnoozed && <div style={{ fontSize: '9px', color: '#856404', marginTop: '4px', fontWeight: 'bold' }}>SNOOZED</div>}
                                                {justSaved && <div style={{ fontSize: '9px', color: '#155724', marginTop: '4px', fontWeight: 'bold' }}>SAVED</div>}
                                            </td>

                                            <td>{item.shivaami_renewal_date || '--'}</td>
                                            <td className="fw-bold text-primary">{item.Domains}</td>
                                            <td style={{ maxWidth: '140px', whiteSpace: 'normal', wordWrap: 'break-word' }}>{item.Plan}</td>
                                            <td>{item.Licenses}</td>
                                            <td>{item.Renewed_Users || '--'}</td>
                                            <td>
                                                <Badge bg="secondary" style={{ color: '#000000' }}>
                                                    {formatOpsStatus(item.Renewal_Status)}
                                                </Badge>
                                            </td>
                                            <td>{item.Google_renewal_date || '--'}</td>
                                            <td style={{ maxWidth: '120px', whiteSpace: 'normal', wordWrap: 'break-word' }}>{item.Renewal_Team_Comments || '--'}</td>
                                            <td style={{ maxWidth: '120px', whiteSpace: 'normal', wordWrap: 'break-word' }}>{item.Special_Comment || '--'}</td>

                                            {/* ─── Ops Status dropdown */}
                                            <td>
                                                <select
                                                    className={`form-select form-select-sm fw-bold ${currentApprovalStatus === 'Approved' ? 'text-success' :
                                                        currentApprovalStatus === 'Rejected' ? 'text-danger' : 'text-dark'
                                                        }`} style={{ color: '#000' }}
                                                    value={currentApprovalStatus}
                                                    onChange={(e) => handleInputChange(item.subscriptionId, 'approval_status', e.target.value)}
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Approved">Approved</option>
                                                    <option value="Rejected">Rejected</option>
                                                </select>
                                            </td>

                                            {/* ─── FIX 2: Single Malvika Comments input — no duplicate column */}
                                            <td>
                                                <input
                                                    className="form-control form-control-sm"
                                                    type="text"
                                                    value={currentMalvikaComment}
                                                    onChange={(e) => handleInputChange(item.subscriptionId, 'Malvika_Comments', e.target.value)}
                                                    placeholder="Ops comment..."
                                                />
                                            </td>

                                            {/* ─── GRD Cases (Actual Date) */}
                                            <td>
                                                <input
                                                    className="form-control form-control-sm"
                                                    type="date"
                                                    value={currentActualDate}
                                                    onChange={(e) => handleInputChange(item.subscriptionId, 'Actual_Date', e.target.value)}
                                                />
                                            </td>

                                            <td className="fw-bold">{extractGMTTime(item.Renewal_GMT)}</td>
                                            <td className="fw-bold text-primary">{convertGMTtoIST(item.Renewal_GMT)}</td>

                                            <td>
                                                <button
                                                    className="btn btn-success btn-sm w-100"
                                                    onClick={() => handleSave(item)}
                                                >
                                                    Save
                                                </button>
                                            </td>

                                            <td>
                                                <button
                                                    className="btn btn-outline-info btn-sm w-100"
                                                    onClick={() => viewHistory(item)}
                                                >
                                                    <i className="fa fa-history"></i> View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan="17" className="text-center py-4">No tasks found for selected dates.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                        <Pagination size="sm">
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            {[...Array(totalPages)].map((_, i) => (
                                <Pagination.Item
                                    key={i + 1}
                                    active={i + 1 === currentPage}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Div>

            {/* HISTORY MODAL */}
            <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fs-5">
                        Operations History: <strong>{historyDomain}</strong>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {historyLogs.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped" style={{ fontSize: '12px' }}>
                                <thead style={{ backgroundColor: '#212529' }}>
                                    <tr>
                                        <th style={{ color: '#100e0e' }}>Date/Time</th>
                                        <th style={{ color: '#100e0e' }}>Status</th>
                                        <th style={{ color: '#100e0e' }}>Comments</th>
                                        <th style={{ color: '#100e0e' }}>GRD Case (Actual Date)</th>
                                        <th style={{ color: '#100e0e' }}>Updated By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyLogs.map((log, i) => (
                                        <tr key={i}>
                                            <td>{new Date(log.updated_at).toLocaleString()}</td>
                                            <td>
                                                <span className={`badge ${log.approval_status === 'Approved' ? 'bg-success' :
                                                    log.approval_status === 'Rejected' ? 'bg-danger' : 'bg-secondary'
                                                    }`}>
                                                    {log.approval_status || 'Pending'}
                                                </span>
                                            </td>
                                            <td>{log.malvika_comments || '--'}</td>
                                            <td>{log.actual_date ? String(log.actual_date).substring(0, 10) : '--'}</td>
                                            <td>{log.updated_by}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">No history logs found.</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistory(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Div>
    );
}